

import { useState, useEffect } from "react";

interface StatusUpdatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  currentStatus: string;
  quotationId: number;
  quotationNumber: string;
  onUpdateStatus: (id: number, newStatus: string) => Promise<void>;
}

const statusOptions = [
  { value: "draft", label: "Draft", color: "bg-stone-100 text-stone-700 border-stone-300", icon: "📝" },
  { value: "sent", label: "Sent", color: "bg-blue-50 text-blue-700 border-blue-300", icon: "📧" },
  { value: "approved", label: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: "✅" },
  { value: "rejected", label: "Rejected", color: "bg-rose-50 text-rose-700 border-rose-300", icon: "❌" },
  { value: "expired", label: "Expired", color: "bg-amber-50 text-amber-700 border-amber-300", icon: "⏰" },
];

export default function StatusUpdatePopup({
  isOpen,
  onClose,
  currentStatus,
  quotationId,
  quotationNumber,
  onUpdateStatus,
}: StatusUpdatePopupProps) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus);
      setError("");
    }
  }, [isOpen, currentStatus]);

  if (!isOpen) return null;

  const handleUpdate = async () => {
    if (selectedStatus === currentStatus) {
      onClose();
      return;
    }

    setIsUpdating(true);
    setError("");

    try {
      await onUpdateStatus(quotationId, selectedStatus);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Popup - Mobile optimized bottom sheet style */}
      <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-sm sm:w-full z-50 bg-white rounded-t-3xl sm:rounded-2xl shadow-xl animate-slide-up">
        {/* Handle for mobile */}
        <div className="flex justify-center pt-3 pb-2 sm:hidden">
          <div className="w-12 h-1.5 bg-stone-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 sm:px-6 pb-3 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-stone-800">Update Status</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full hover:bg-stone-100 transition-colors"
            >
              <svg className="w-5 h-5 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-stone-500 mt-1">
            {quotationNumber} • Current: <span className="font-medium capitalize">{currentStatus}</span>
          </p>
        </div>

        {/* Status Options */}
        <div className="px-4 sm:px-6 py-4">
          <div className="grid grid-cols-2 gap-2.5">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedStatus(option.value)}
                className={`relative flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                  selectedStatus === option.value
                    ? `${option.color} border-current ring-2 ring-offset-1 ring-current/20`
                    : "bg-white border-stone-200 hover:border-stone-300 text-stone-700"
                }`}
              >
                <span className="text-xl">{option.icon}</span>
                <span className="font-medium text-sm">{option.label}</span>
                {selectedStatus === option.value && (
                  <svg className="absolute top-2 right-2 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-3 p-3 bg-rose-50 text-rose-700 rounded-xl text-sm flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="flex-1 px-4 py-3 rounded-xl gold-gradient text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
          >
            {isUpdating ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </button>
        </div>
      </div>
    </>
  );
}
